import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API } from '../../../contexts/AuthContext';

// Uma linha por canal: nome, grupo, ordem e se aparece no portal. As edicoes
// ficam num rascunho e sao salvas juntas ("Salvar alteracoes").

export default function CamerasAba({ onAlterou }) {
  const [cameras, setCameras] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [gravadores, setGravadores] = useState([]);
  const [rascunho, setRascunho] = useState({}); // id -> campos alterados
  const [filtroEquip, setFiltroEquip] = useState('');
  const [busca, setBusca] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  const carregar = useCallback(async () => {
    try {
      const [cam, grav] = await Promise.all([
        axios.get(`${API}/cameras/cameras`),
        axios.get(`${API}/cameras/gravadores`),
      ]);
      setCameras(cam.data?.data?.cameras || []);
      setGrupos(cam.data?.data?.grupos || []);
      setGravadores(grav.data?.data?.gravadores || []);
      setErro('');
    } catch (e) {
      setErro(e.response?.data?.message || 'Não foi possível carregar as câmeras.');
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const gravadoresPorId = useMemo(() => new Map(gravadores.map((g) => [g.id, g])), [gravadores]);

  const valor = (c, campo) => (rascunho[c.id] && campo in rascunho[c.id] ? rascunho[c.id][campo] : c[campo]);
  const alterar = (c, campo, v) => setRascunho((r) => ({ ...r, [c.id]: { ...r[c.id], [campo]: v } }));

  const visiveis = cameras.filter((c) => {
    if (filtroEquip && String(c.gravador_id) !== filtroEquip) return false;
    const termo = busca.trim().toLowerCase();
    if (!termo) return true;
    return `${c.nome_exibicao} ${gravadoresPorId.get(c.gravador_id)?.nome || ''}`.toLowerCase().includes(termo);
  });

  const pendentes = Object.keys(rascunho);

  const salvar = async () => {
    setSalvando(true);
    setErro('');
    try {
      for (const id of pendentes) {
        const atual = cameras.find((c) => String(c.id) === id);
        if (!atual) continue;
        const final = { ...atual, ...rascunho[id] };
        await axios.put(`${API}/cameras/cameras/${id}`, {
          nome: final.nome || '',
          grupo_id: final.grupo_id || null,
          ativo: !!final.ativo,
          ordem: Number(final.ordem) || 0,
        });
      }
      setRascunho({});
      await carregar();
      onAlterou();
    } catch (e) {
      setErro(e.response?.data?.message || 'Não foi possível salvar. As linhas não salvas continuam marcadas.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <>
      {erro ? <div className="feedback-banner feedback-banner--error" style={{ marginBottom: 12 }}>{erro}</div> : null}
      <div className="cameras-config__topo">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <select value={filtroEquip} onChange={(e) => setFiltroEquip(e.target.value)} className="cameras-config__select">
            <option value="">Todos os equipamentos</option>
            {gravadores.map((g) => <option key={g.id} value={g.id}>{g.nome}</option>)}
          </select>
          <input
            type="search"
            className="cameras-config__select"
            placeholder="Buscar câmera"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {pendentes.length > 0 && (
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setRascunho({})} disabled={salvando}>
              Descartar
            </button>
          )}
          <button type="button" className="btn btn-primary btn-sm" onClick={salvar} disabled={!pendentes.length || salvando}>
            <i className="bi bi-save me-1" />
            {salvando ? 'Salvando…' : `Salvar alterações${pendentes.length ? ` (${pendentes.length})` : ''}`}
          </button>
        </div>
      </div>

      {cameras.length === 0 ? (
        <div className="empty-state">
          <strong>Nenhuma câmera</strong>
          <span>Cadastre um equipamento na aba Equipamentos: as câmeras aparecem aqui sozinhas.</span>
        </div>
      ) : (
        <div className="table-shell">
          <table className="portal-table">
            <thead>
              <tr>
                <th>Equipamento</th>
                <th style={{ width: 60 }}>Canal</th>
                <th>Nome</th>
                <th>Grupo</th>
                <th style={{ width: 80 }}>Ordem</th>
                <th style={{ width: 70 }}>Exibir</th>
              </tr>
            </thead>
            <tbody>
              {visiveis.map((c) => (
                <tr key={c.id} className={rascunho[c.id] ? 'cameras-config__alterada' : undefined}>
                  <td className="cell-muted">{gravadoresPorId.get(c.gravador_id)?.nome || '—'}</td>
                  <td>{c.canal}</td>
                  <td>
                    <input
                      type="text"
                      className="cameras-config__input"
                      maxLength={100}
                      value={valor(c, 'nome') || ''}
                      placeholder={`CH ${String(c.canal).padStart(2, '0')}`}
                      onChange={(e) => alterar(c, 'nome', e.target.value)}
                    />
                  </td>
                  <td>
                    <select
                      className="cameras-config__input"
                      value={valor(c, 'grupo_id') || ''}
                      onChange={(e) => alterar(c, 'grupo_id', e.target.value ? Number(e.target.value) : null)}
                    >
                      <option value="">— Sem grupo —</option>
                      {grupos.map((g) => <option key={g.id} value={g.id}>{g.nome}</option>)}
                    </select>
                  </td>
                  <td>
                    <input
                      type="number"
                      className="cameras-config__input"
                      value={valor(c, 'ordem')}
                      onChange={(e) => alterar(c, 'ordem', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={!!valor(c, 'ativo')}
                      onChange={(e) => alterar(c, 'ativo', e.target.checked)}
                      title="Câmera desmarcada some do mosaico, da lista e dos tours"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
